import type { ReactNode } from 'react';
import { lastValue } from '../../values/readings.ts';
import { TILE_FONT_FAMILY } from '../../view/theme.ts';
import { useTileTextColor } from '../../view/tile_style.ts';
import { useTileEnvironment } from '../../view/tile_environment.ts';
import { DEFAULT_VALUE_COLOR } from '../value/value_tile.ts';
import { readsSource, textTileContent, type TextTile } from './text_tile.ts';

const JUSTIFY = { top: 'flex-start', center: 'center', bottom: 'flex-end' } as const;

/**
 * The content area of a `text` tile (docs/specs/text-tile.md): the text on the page, not on a
 * canvas, so that wrapping and alignment are the browser's (TX-5); cut off at the tile's edges.
 */
export function TextTileView({ tile }: { tile: TextTile }) : ReactNode {
    const environment = useTileEnvironment();
    const value = lastValue(environment.useReadings(tile, readsSource(tile) ? tile.options.source : undefined));
    const content = textTileContent(tile, value, environment.locale, useTileTextColor(DEFAULT_VALUE_COLOR));

    return (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: JUSTIFY[content.valign], overflow: 'hidden' }}>
            <div
                style={{
                    color: content.color,
                    font: `${content.size}px/1.2 ${TILE_FONT_FAMILY}`,
                    textAlign: content.align,
                    // Line breaks in the text are kept; off, a line stays one line.
                    whiteSpace: content.wrap ? 'pre-wrap' : 'pre',
                    overflowWrap: content.wrap ? 'anywhere' : 'normal',
                    overflow: 'hidden',
                }}
            >
                {content.text}
            </div>
        </div>
    );
}
